import * as React from "react";
import { Badge, Button, Card, Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { gettext } from "i18n";
import { timestampToDatetimeString } from "utils/formatting";
import {
  METHOD_LABELS,
  MfaMethod,
  MfaStatus,
  deleteMethod,
  describeRequirement,
  disableMfa,
  loadMfaStatus,
  regenerateBackupCodes,
  setPreferredMethod,
} from "utils/mfa";
import BackupCodesDisplay from "./BackupCodesDisplay";
import MfaMethodSetup from "./MfaMethodSetup";
import {
  StepUpCancelled,
  useConfirmSecondFactor,
  useStepUp,
} from "./StepUpProvider";

/**
 * The user's multi-factor authentication: their sign-in methods, which one the
 * login asks for first, backup codes, and turning MFA off where it is optional.
 */
const SecuritySettings: React.FC = () => {
  const withStepUp = useStepUp();
  const confirmSecondFactor = useConfirmSecondFactor();
  const [status, setStatus] = React.useState<MfaStatus | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState<boolean>(false);
  const [backupCodes, setBackupCodes] = React.useState<string[] | null>(null);

  const refresh = React.useCallback(async () => {
    try {
      setStatus(await loadMfaStatus());
      setLoadError(null);
    } catch (e) {
      setLoadError(gettext((e as Error).message));
    }
  }, []);

  React.useEffect(() => {
    refresh();
  }, [refresh]);

  // Runs a change that may need the second factor confirmed first
  const change = async (action: () => Promise<any>, success: string) => {
    try {
      const result = await withStepUp(action);
      toast.success(success);
      await refresh();
      return result;
    } catch (e) {
      if (!(e instanceof StepUpCancelled)) {
        toast.error(gettext((e as Error).message));
      }
      return undefined;
    }
  };

  const startAdding = async () => {
    // Adding a method to an account that has MFA needs the second factor first
    if (status?.enabled && !status.recently_verified) {
      try {
        await confirmSecondFactor();
      } catch (e) {
        if (!(e instanceof StepUpCancelled)) {
          toast.error(gettext((e as Error).message));
        }
        return;
      }
    }
    setAdding(true);
  };

  const onAdded = async (codes: string[] | null) => {
    setAdding(false);
    toast.success(gettext("Sign-in method added"));
    if (codes) {
      setBackupCodes(codes);
    }
    await refresh();
  };

  const onDelete = async (method: MfaMethod) => {
    if (
      !window.confirm(
        gettext(
          "Are you sure you want to remove %1?",
          method.label || gettext(METHOD_LABELS[method.kind]),
        ),
      )
    ) {
      return;
    }
    await change(
      () => deleteMethod(method.id),
      gettext("Sign-in method removed"),
    );
  };

  const onRegenerate = async () => {
    if (
      !window.confirm(
        gettext(
          "Create new backup codes? Your current backup codes will stop working.",
        ),
      )
    ) {
      return;
    }
    const codes = await change(
      () => regenerateBackupCodes(),
      gettext("New backup codes created"),
    );
    if (codes) {
      setBackupCodes(codes);
    }
  };

  const onDisable = async () => {
    if (
      !window.confirm(
        gettext(
          "Turn off multi-factor authentication? Logging in will then only need your password.",
        ),
      )
    ) {
      return;
    }
    await change(
      () => disableMfa(),
      gettext("Multi-factor authentication turned off"),
    );
  };

  if (loadError) {
    return <p className="text-danger">{loadError}</p>;
  }
  if (!status) {
    return <p>{gettext("Loading...")}</p>;
  }

  return (
    <div className="SecuritySettings">
      <Card className="mb-3">
        <Card.Body>
          <h5>
            {gettext("Multi-factor authentication")}{" "}
            {status.enabled ? (
              <Badge bg="success">{gettext("On")}</Badge>
            ) : (
              <Badge bg={status.required ? "danger" : "secondary"}>
                {gettext("Off")}
              </Badge>
            )}
          </h5>
          <p className="text-muted">
            {gettext(
              "Besides your password, logging in needs a second factor: a code from an app, a passkey or a code sent to your email. Someone who learns your password still cannot log in.",
            )}
          </p>
          {status.required_reasons.map((reason, index) => (
            <p key={`reason_${index}`} className="mb-1">
              <strong>{describeRequirement(reason)}</strong>
            </p>
          ))}

          {status.methods.length > 0 && (
            <Table responsive className="mt-3">
              <thead>
                <tr>
                  <th>{gettext("Method")}</th>
                  <th>{gettext("Name")}</th>
                  <th>{gettext("Added")}</th>
                  <th>{gettext("Last used")}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {status.methods.map((method) => (
                  <tr key={`mfa_method_${method.id}`}>
                    <td>
                      {gettext(METHOD_LABELS[method.kind])}{" "}
                      {status.preferred_method === method.kind && (
                        <Badge bg="primary">{gettext("Preferred")}</Badge>
                      )}
                      {!method.allowed && (
                        <Badge bg="warning" text="dark">
                          {gettext("Not allowed for your account")}
                        </Badge>
                      )}
                    </td>
                    <td>{method.label}</td>
                    <td>
                      {method.created_at
                        ? timestampToDatetimeString(method.created_at)
                        : ""}
                    </td>
                    <td>
                      {method.last_used_at
                        ? timestampToDatetimeString(method.last_used_at)
                        : ""}
                    </td>
                    <td className="text-end text-nowrap">
                      {status.preferred_method !== method.kind && (
                        <Button
                          variant="link"
                          size="sm"
                          onClick={() =>
                            change(
                              () => setPreferredMethod(method.kind),
                              gettext("Preferred method changed"),
                            )
                          }
                        >
                          {gettext("Make preferred")}
                        </Button>
                      )}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => onDelete(method)}
                      >
                        {gettext("Remove")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}

          <div className="d-flex flex-wrap gap-2 mt-3">
            <Button onClick={startAdding}>
              {status.enabled
                ? gettext("Add a sign-in method")
                : gettext("Turn on multi-factor authentication")}
            </Button>
            {status.enabled && !status.required && (
              <Button variant="outline-danger" onClick={onDisable}>
                {gettext("Turn off")}
              </Button>
            )}
          </div>
        </Card.Body>
      </Card>

      {status.enabled && (
        <Card className="mb-3">
          <Card.Body>
            <h5>{gettext("Backup codes")}</h5>
            <p>
              {gettext(
                "You have %1 unused backup codes for logging in if you lose your other sign-in methods.",
                String(status.backup_codes_remaining),
              )}
            </p>
            <Button variant="outline-secondary" onClick={onRegenerate}>
              {gettext("Create new backup codes")}
            </Button>
          </Card.Body>
        </Card>
      )}

      <Modal show={adding} onHide={() => setAdding(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{gettext("Add a sign-in method")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {adding && (
            <MfaMethodSetup
              allowedMethods={status.allowed_methods}
              existingMethods={status.methods.map((method) => method.kind)}
              onAdded={onAdded}
              onCancel={() => setAdding(false)}
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal show={!!backupCodes} backdrop="static" centered>
        <Modal.Header>
          <Modal.Title>{gettext("Your backup codes")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {backupCodes && (
            <BackupCodesDisplay
              codes={backupCodes}
              onDone={() => setBackupCodes(null)}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SecuritySettings;
